import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ArrowUpRight, ChevronDown, ExternalLink, Search, SlidersHorizontal, X } from 'lucide-react'
import coreData from '../data/coreMaster.json'
import coreAvatarIds from '../data/coreAvatarIds.json'
import { coreSmartHomeBrandAudit } from '../data/coreBrandAudit.js'
import { historicalRelationshipNames, j200RelationshipNames, verifiedRelationshipAliasNames } from '../data/relationshipNames.js'

const PLATFORM_LABELS = { youtube: 'YouTube', instagram: 'Instagram', tiktok: 'TikTok' }
const TIER_LABELS = { emerging: '5千–5万', mid: '5万–30万', upper_mid: '30万–100万', head: '100万+' }
const integerFormatter = new Intl.NumberFormat('zh-CN')
const usdFormatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })
const LOCAL_WORKBOOK_URL = `${import.meta.env.BASE_URL}report-data/Aqara_日本达人资源数据底表_20260722.xlsx`
const cx = (...classes) => classes.filter(Boolean).join(' ')
const EXCLUDED_CREATOR_NAMES = new Set([
  'YASUOJAPAN / 飯島康夫',
  'JSK-koubou',
  'パソコン博士TAIKI',
  'KinoMa',
  'Ohouse オーハウス | インテリア・家具',
  'リコメン堂',
])

function normalize(value) {
  return String(value || '').normalize('NFKC').toLocaleLowerCase('ja-JP').replace(/[^\p{L}\p{N}]/gu, '')
}

function getIdentityKey(value) {
  const normalized = normalize(value)
  if (normalized.startsWith('ツヨシ') || normalized.includes('techyou')) return 'ツヨシtechyou'
  if (normalized.startsWith('家電彼女')) return '家電彼女'
  return normalized
}

const knownRelationshipNames = new Set(
  [...historicalRelationshipNames, ...j200RelationshipNames, ...verifiedRelationshipAliasNames].map(normalize),
)

const smartHomeBrandMap = new Map(
  coreSmartHomeBrandAudit.map((record) => [getIdentityKey(record.name), record]),
)

const avatarMap = new Map(
  coreAvatarIds.map((id) => [id, `${import.meta.env.BASE_URL}avatars/core/${id}.jpg`]),
)

function formatCount(value) {
  const number = Number(value)
  if (!Number.isFinite(number)) return '—'
  if (number >= 100000000) return `${(number / 100000000).toFixed(1).replace(/\.0$/, '')}亿`
  if (number >= 10000) return `${(number / 10000).toFixed(number >= 100000 ? 1 : 2).replace(/\.0$/, '')}万`
  return integerFormatter.format(number)
}

function formatPercent(value) {
  const number = Number(value)
  if (!Number.isFinite(number)) return '—'
  return `${(number <= 1 ? number * 100 : number).toFixed(1)}%`
}

function getTier(creator) {
  return Array.isArray(creator.tiers) ? creator.tiers[0] : creator.tiers
}

function getAverageViews(creator) {
  return creator.profile?.avg_views ?? creator.avg_views
}

function getMedianViews(creator) {
  return creator.profile?.median_views ?? creator.median_views
}

function getScore(creator) {
  return Number(creator.decision_score ?? creator.final_score ?? 0) || 0
}

function getTags(creator) {
  const accounts = creator.accounts?.length ? creator.accounts : [creator]
  return [...new Set(accounts.flatMap((account) => [
    ...(account.campaign_directions || []),
    ...(account.content?.top_tags || []),
    ...(account.tags || []),
  ]))].filter(Boolean)
}

function hasRelationship(creator) {
  const accounts = creator.accounts?.length ? creator.accounts : [creator]
  return accounts.some((account) => knownRelationshipNames.has(normalize(account.nickname)))
}

function getVerdict(creator) {
  return creator.final_verdict || creator.preliminary_verdict || '后期可考虑'
}

function getSmartHomeAudit(creator) {
  const accounts = creator.accounts?.length ? creator.accounts : [creator]
  for (const account of accounts) {
    const record = smartHomeBrandMap.get(getIdentityKey(account.nickname))
    if (record) return record
  }
  return smartHomeBrandMap.get(getIdentityKey(creator.nickname)) || null
}

const creatorSummaryMap = new Map([
  ['カズチャンネル/Kazu Channel', '头部数码／智能家居达人；有智能锁公开内容'],
  ['かじがや電器店', 'Apple／数码头部，适合可信体验与功能讲解'],
  ['ワタナベカズマサ', 'Aqara 历史合作；泛数码影响力稳定'],
  ['マメ', 'Aqara 已合作；Apple／智能家居垂类，受众精准'],
  ['よいこチャンネル / 岩間よいこ', '家电与生活方式达人，适合家庭使用场景'],
  ['ayumi｜建売×DIY×海外インテリア', '建房／DIY／海外家居内容，适合住宅场景'],
  ['なつこ⌂賃貸暮らし×100均DIY×子育て', '租房 DIY 达人，适合后装与轻安装场景'],
].map(([name, summary]) => [getIdentityKey(name), summary]))

function getCreatorSummary(creator) {
  const manualSummary = creatorSummaryMap.get(getIdentityKey(creator.nickname))
  if (manualSummary) return manualSummary

  const tags = getTags(creator).map((tag) => String(tag).toLocaleLowerCase('ja-JP'))
  const tagText = tags.join(' ')
  const related = hasRelationship(creator)
  const brands = getSmartHomeAudit(creator)?.brands || []
  const averageViews = Number(getAverageViews(creator) || 0)
  const appleFocused = /apple|iphone|ipad|mac|homekey|apple_home/.test(tagText)
  const smartHomeFocused = /smart.home|smart.lock|智能家居|门锁|ガジェット/.test(tagText) || brands.length > 0
  const homeFocused = /home|diy|interior|インテリア|暮らし|生活|家居|家電/.test(tagText)

  const parts = []
  if (related) parts.push('Aqara 已合作')
  if (appleFocused) parts.push('Apple／数码垂类')
  else if (smartHomeFocused) parts.push('智能家居／数码垂类')
  else if (homeFocused) parts.push('家居生活／DIY 内容')
  else parts.push(`${PLATFORM_LABELS[creator.platform] || creator.platform} 内容创作者`)

  if (averageViews >= 100000) parts.push('播放表现突出')
  else if (brands.length) parts.push(`有 ${brands.slice(0, 2).join('、')} 合作记录`)
  else if (appleFocused) parts.push('适合 Apple 家庭场景')
  else if (homeFocused) parts.push('适合家庭使用场景')
  else parts.push('内容方向与 Aqara 相关')
  return parts.join('；')
}

function mergeCoreCreators() {
  const groups = new Map()
  for (const account of coreData) {
    if (EXCLUDED_CREATOR_NAMES.has(account.nickname)) continue
    const key = getIdentityKey(account.nickname)
    const accounts = groups.get(key) || []
    accounts.push(account)
    groups.set(key, accounts)
  }

  return [...groups.values()].map((accounts) => {
    const primary = accounts.reduce((best, account) => (
      Number(account.followers || 0) > Number(best.followers || 0) ? account : best
    ))
    const evaluationSource = accounts.reduce((best, account) => (
      getScore(account) > getScore(best) ? account : best
    ))
    return {
      ...primary,
      decision_score: getScore(evaluationSource),
      final_score: evaluationSource.final_score,
      final_verdict: evaluationSource.final_verdict,
      preliminary_verdict: evaluationSource.preliminary_verdict,
      recommendation_reason: evaluationSource.recommendation_reason,
      platforms: [primary.platform, ...new Set(accounts.map((account) => account.platform).filter((platform) => platform !== primary.platform))],
      accountIds: accounts.map((account) => account.id),
      accounts,
      avatar: avatarMap.get(primary.id) || accounts.map((account) => avatarMap.get(account.id)).find(Boolean) || null,
    }
  })
}

const CORE_CREATORS = mergeCoreCreators()
const CORE_IDENTITY_COUNT = CORE_CREATORS.length

function CreatorAvatar({ creator, size = 'normal' }) {
  const [failed, setFailed] = useState(false)
  if (!creator.avatar || failed) {
    return <span className={`creator-avatar avatar-placeholder avatar-${size}`} aria-hidden="true">{[...creator.nickname][0]}</span>
  }
  return <img className={`creator-avatar avatar-${size}`} src={creator.avatar} alt="" loading="lazy" onError={() => setFailed(true)} />
}

function PlatformMark({ platform }) {
  return <span className={`platform-mark platform-${platform}`}><i />{PLATFORM_LABELS[platform] || platform}</span>
}

function CreatorDrawer({ creator, onClose, triggerRef }) {
  const dialogRef = useRef(null)
  const closeRef = useRef(null)
  const tags = getTags(creator).slice(0, 10)
  const smartHomeAudit = getSmartHomeAudit(creator)
  const smartHomeBrands = smartHomeAudit?.brands || []
  const profileUrl = creator.channel_url || creator.profile?.channel_url || creator.social_media?.[0]?.url
  const relationship = hasRelationship(creator)

  useEffect(() => {
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    requestAnimationFrame(() => closeRef.current?.focus())
    const onKeyDown = (event) => {
      if (event.key === 'Escape') onClose()
      if (event.key !== 'Tab') return
      const items = dialogRef.current?.querySelectorAll('button, a[href], summary, [tabindex]:not([tabindex="-1"])')
      if (!items?.length) return
      const first = items[0]
      const last = items[items.length - 1]
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.body.style.overflow = previous
      triggerRef.current?.focus?.()
    }
  }, [onClose, triggerRef])

  return (
    <div className="drawer-backdrop" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <aside className="creator-drawer" role="dialog" aria-modal="true" aria-labelledby="drawer-title" ref={dialogRef}>
        <button className="drawer-close" type="button" onClick={onClose} ref={closeRef} aria-label="关闭达人详情"><X size={20} /></button>
        <header className="drawer-profile">
          <CreatorAvatar creator={creator} size="large" />
          <div>
            <span>{creator.platforms.map((platform) => PLATFORM_LABELS[platform]).join(' · ')}</span>
            <h3 id="drawer-title">{creator.nickname}</h3>
            <div className="tag-list">{tags.slice(0, 4).map((tag) => <b key={tag}>{tag}</b>)}</div>
          </div>
        </header>

        <div className="drawer-kpis">
          <div><span>粉丝</span><strong>{formatCount(creator.followers)}</strong></div>
          <div><span>均播</span><strong>{formatCount(getAverageViews(creator))}</strong></div>
          <div><span>中位数</span><strong>{formatCount(getMedianViews(creator))}</strong></div>
          <div><span>是否合作过</span><strong>{relationship ? '是' : '否'}</strong></div>
        </div>

        <section className="drawer-evaluation">
          <span>AI 评价 · {getVerdict(creator)}</span>
          <p>{creator.recommendation_reason || '已完成核心数据采集，仍需结合品牌内容、档期与正式报价进行人工判断。'}</p>
        </section>

        <section className="drawer-block drawer-smart-home-records">
          <h4>智能家居品牌记录</h4>
          {smartHomeBrands.length ? (
            <>
              <div className="brand-list smart-home-brand-list">{smartHomeBrands.map((brand) => <span key={brand}>{brand}</span>)}</div>
              {smartHomeAudit.evidenceUrl ? (
                <a className="drawer-smart-home-evidence" href={smartHomeAudit.evidenceUrl} target="_blank" rel="noreferrer">
                  <span>{smartHomeAudit.evidenceTitle}</span>
                  <strong>相关品牌公开内容最高 {formatCount(smartHomeAudit.peakViews)} 播放</strong>
                  <ExternalLink size={16} aria-hidden="true" />
                </a>
              ) : <p className="brand-evidence-note">来自 Nox 合作品牌聚合记录；暂无公开视频级播放数证据。</p>}
            </>
          ) : <p>未在已保存的聚合数据或公开 A 类证据中检出，不代表从未合作。</p>}
        </section>

        {creator.accounts.length > 1 ? (
          <section className="drawer-block">
            <h4>平台账号明细</h4>
            <div className="account-breakdown">
              {creator.accounts.map((account) => (
                <div key={account.id}>
                  <PlatformMark platform={account.platform} />
                  <span>粉丝 {formatCount(account.followers)}</span>
                  <span>均播 {formatCount(getAverageViews(account))}</span>
                  <span>中位数 {formatCount(getMedianViews(account))}</span>
                </div>
              ))}
            </div>
          </section>
        ) : null}

        <section className="drawer-block">
          <h4>品牌合作表现</h4>
          <div className="drawer-performance">
            <div><span>品牌内容均播</span><strong>{formatCount(creator.ad_video_avg_views)}</strong></div>
            <div><span>互动率</span><strong>{formatPercent(creator.brand_video_engagement_rate)}</strong></div>
            <div><span>Nox 预估费用</span><strong>{Number.isFinite(creator.estimated_price_avg) ? usdFormatter.format(creator.estimated_price_avg) : '—'}</strong></div>
          </div>
        </section>

        <details className="drawer-block drawer-source">
          <summary>数据说明 <ChevronDown size={17} /></summary>
          <p>核心商业评估 · 数据采集 2026.07.21。费用为 Nox 算法估值，不是达人正式报价；品牌记录合并 Nox 聚合与公开 A 类商业披露证据，关系判断以历史 tracker、J200 已发布记录、主页 ID 与人工确认结果为准。</p>
        </details>

        <div className="drawer-actions">
          {profileUrl ? <a href={profileUrl} target="_blank" rel="noreferrer">打开达人主页 <ExternalLink size={17} /></a> : <span>主页暂未采集</span>}
          <a className="workbook-local-link" href={LOCAL_WORKBOOK_URL} download>下载本地统一数据表 <ArrowUpRight size={17} /></a>
        </div>
      </aside>
    </div>
  )
}

export default function CreatorExplorer() {
  const triggerRef = useRef(null)
  const creators = CORE_CREATORS
  const [search, setSearch] = useState('')
  const [platform, setPlatform] = useState('all')
  const [tier, setTier] = useState('all')
  const [relationship, setRelationship] = useState('all')
  const [sort, setSort] = useState('relationship')
  const [selected, setSelected] = useState(null)
  const [visibleCount, setVisibleCount] = useState(12)

  const filtered = useMemo(() => {
    const needle = normalize(search)
    const result = creators.filter((creator) => {
      const smartHomeBrands = getSmartHomeAudit(creator)?.brands || []
      const searchable = normalize([creator.nickname, ...getTags(creator), ...smartHomeBrands].join(' '))
      const creatorTier = getTier(creator)
      const related = hasRelationship(creator)
      return (!needle || searchable.includes(needle))
        && (platform === 'all' || creator.platforms.includes(platform))
        && (tier === 'all' || creatorTier === tier)
        && (relationship === 'all' || (relationship === 'yes' ? related : !related))
    })
    return [...result].sort((a, b) => {
      if (sort === 'relationship') {
        const relationshipDifference = Number(hasRelationship(b)) - Number(hasRelationship(a))
        if (relationshipDifference) return relationshipDifference
      }
      if (sort === 'followers') return (b.followers || 0) - (a.followers || 0)
      if (sort === 'views') return Number(getAverageViews(b) || 0) - Number(getAverageViews(a) || 0)
      return getScore(b) - getScore(a)
    })
  }, [creators, platform, relationship, search, sort, tier])

  useEffect(() => setVisibleCount(12), [platform, relationship, search, sort, tier])

  const openCreator = useCallback((creator, element) => {
    triggerRef.current = element
    setSelected(creator)
  }, [])

  const closeCreator = useCallback(() => setSelected(null), [])

  return (
    <div className="creator-explorer" data-reveal>
      <div className="creator-toolbar">
        <label className="creator-search"><Search size={17} /><span className="sr-only">搜索达人或标签</span><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="搜索账号、标签或关键词" /></label>
        <label><span>主平台</span><select value={platform} onChange={(event) => setPlatform(event.target.value)}><option value="all">全部平台</option><option value="youtube">YouTube</option><option value="instagram">Instagram</option><option value="tiktok">TikTok</option></select></label>
        <label><span>粉丝量</span><select value={tier} onChange={(event) => setTier(event.target.value)}><option value="all">全部量级</option>{Object.entries(TIER_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
        <label><span>是否合作过</span><select value={relationship} onChange={(event) => setRelationship(event.target.value)}><option value="all">全部关系</option><option value="yes">合作过</option><option value="no">未合作</option></select></label>
        <label><span>排序</span><select value={sort} onChange={(event) => setSort(event.target.value)}><option value="relationship">合作过优先</option><option value="score">推荐顺序</option><option value="followers">粉丝量</option><option value="views">均播</option></select></label>
        <span className="filter-symbol" aria-hidden="true"><SlidersHorizontal size={18} /></span>
      </div>

      <div className="creator-result-count" aria-live="polite"><strong>{filtered.length}</strong> / {CORE_IDENTITY_COUNT} 位后期可考虑合作资源</div>

      <div className="creator-table-shell">
        <table className="creator-table">
          <thead><tr><th>头像</th><th>账号</th><th>主平台</th><th>粉丝</th><th>均播</th><th>标签</th><th>智能家居品牌</th><th>是否合作过</th><th>NOX 预估费用</th><th><span className="sr-only">操作</span></th></tr></thead>
          <tbody>
            {filtered.slice(0, visibleCount).map((creator) => {
              const tags = getTags(creator).slice(0, 3)
              const smartHomeBrands = getSmartHomeAudit(creator)?.brands || []
              const related = hasRelationship(creator)
              return (
                <tr key={creator.accountIds.join('-')}>
                  <td><CreatorAvatar creator={creator} /></td>
                  <th><button type="button" onClick={(event) => openCreator(creator, event.currentTarget)}>{creator.nickname}<small>{getCreatorSummary(creator)}</small></button></th>
                  <td><PlatformMark platform={creator.platform} />{creator.platforms.length > 1 ? <small className="platform-more">+{creator.platforms.length - 1}</small> : null}</td>
                  <td>{formatCount(creator.followers)}</td>
                  <td>{formatCount(getAverageViews(creator))}</td>
                  <td><div className="table-tags">{tags.map((tag) => <span key={tag}>{tag}</span>)}</div></td>
                  <td className="smart-brand-column">
                    {smartHomeBrands.length ? (
                      <div className="table-tags smart-brand-list">
                        {smartHomeBrands.slice(0, 3).map((brand) => <span key={brand}>{brand}</span>)}
                        {smartHomeBrands.length > 3 ? <span>+{smartHomeBrands.length - 3}</span> : null}
                      </div>
                    ) : <span className="smart-brand-empty">—</span>}
                  </td>
                  <td><span className={cx('relationship-state', related && 'related')}>{related ? '是' : '否'}</span></td>
                  <td className="nox-price-cell">{Number.isFinite(creator.estimated_price_avg) ? usdFormatter.format(creator.estimated_price_avg) : '—'}</td>
                  <td><button className="table-open" type="button" onClick={(event) => openCreator(creator, event.currentTarget)} aria-label={`查看 ${creator.nickname} 详情`}><ArrowUpRight size={17} /></button></td>
                </tr>
              )
            })}
            {!filtered.length ? <tr><td className="creator-empty-state" colSpan="10">没有匹配当前筛选条件的达人</td></tr> : null}
          </tbody>
        </table>
      </div>

      {visibleCount < filtered.length ? <button className="show-more" type="button" onClick={() => setVisibleCount((count) => count + 12)}>显示更多 <ChevronDown size={17} /></button> : null}

      <details className="creator-method-note">
        <summary>筛选与数据说明 <ChevronDown size={17} /></summary>
        <p>原始核心池剔除 6 个内容或账号类型明显错配的账号后保留 30 个平台账号；“家電彼女”和“ツヨシ”各有两个平台账号，合并为 28 位达人身份。其中 16 位在已保存的 Nox 聚合或公开 A 类证据中检出智能家居品牌记录；关系状态以 2024—2025 tracker、2026 J200 已发布记录及人工确认结果为准，候选分类不等于已合作。NOX 预估费用为平台算法估值，并非达人正式报价。</p>
      </details>

      {selected ? <CreatorDrawer creator={selected} onClose={closeCreator} triggerRef={triggerRef} /> : null}
    </div>
  )
}
